import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import {
  formatHoursLine,
  formatTimeRange,
  isDateInViewRange,
  isSameMonth,
  isWeekend,
  parseHours,
  STATUS_CLASS,
  STATUS_LABELS,
  type DayRecord,
  type DayStatus
} from './calendarShared';

export type CalendarViewMode = 'monthly' | 'range';

export type CellKind = DayStatus | 'Weekend' | 'OutOfRange' | 'Padding';

export interface CalendarCell {
  date: Date;
  kind: CellKind;
  label: string;
  timeDisplay: string;
  hoursDisplay: string;
  isPadding: boolean;
  record?: DayRecord;
}

export interface MonthSection {
  month: Date;
  monthKey: string;
  cells: CalendarCell[];
}

export interface CalendarSummary {
  officeDays: number;
  leaves: number;
  absent: number;
  totalHours: number;
}

const OFFICE_DAY_STATUSES: DayStatus[] = ['Present', 'ShortDay', 'VeryShort'];

export const TOOLTIP_WIDTH = 196;
export const TOOLTIP_HEIGHT = 132;
export const TOOLTIP_GAP = 8;

export const hasPunchDetails = (record: DayRecord) =>
  OFFICE_DAY_STATUSES.includes(record.status)
  || record.arrival !== '—'
  || record.departure !== '—'
  || record.hours !== '—';

export const buildMonthCells = (
  month: Date,
  recordsByDate: Map<string, DayRecord>,
  rangeStart: Date,
  rangeEnd: Date
): CalendarCell[] => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  return eachDayOfInterval({ start: gridStart, end: gridEnd }).map((date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const inCurrentMonth = isSameMonth(date, month);
    const inViewRange = isDateInViewRange(date, rangeStart, rangeEnd);

    if (!inCurrentMonth) {
      return {
        date,
        kind: 'Padding' as const,
        label: '',
        timeDisplay: '',
        hoursDisplay: '',
        isPadding: true
      };
    }

    if (!inViewRange) {
      return {
        date,
        kind: 'OutOfRange' as const,
        label: '',
        timeDisplay: '',
        hoursDisplay: '',
        isPadding: false
      };
    }

    if (isWeekend(date)) {
      return {
        date,
        kind: 'Weekend' as const,
        label: 'Weekend',
        timeDisplay: '',
        hoursDisplay: '',
        isPadding: false
      };
    }

    const record = recordsByDate.get(dateKey);

    if (!record) {
      return {
        date,
        kind: 'Missing' as const,
        label: STATUS_LABELS.Missing,
        timeDisplay: '',
        hoursDisplay: '',
        isPadding: false
      };
    }

    return {
      date,
      kind: record.status,
      label: STATUS_LABELS[record.status],
      timeDisplay: formatTimeRange(record),
      hoursDisplay: formatHoursLine(record),
      isPadding: false,
      record
    };
  });
};

export const computeSummary = (records: DayRecord[]): CalendarSummary =>
  records.reduce(
    (acc, record) => {
      if (OFFICE_DAY_STATUSES.includes(record.status)) {
        acc.officeDays += 1;
        acc.totalHours += parseHours(record.hours);
      }
      if (
        record.status === 'FullDayLeave' ||
        record.status === 'HalfDayLeave'
      ) {
        acc.leaves += 1;
      }
      if (record.status === 'Missing') acc.absent += 1;
      return acc;
    },
    { officeDays: 0, leaves: 0, absent: 0, totalHours: 0 }
  );

export const getRecordsInRange = (
  recordsByDate: Map<string, DayRecord>,
  rangeStart: Date,
  rangeEnd: Date
) => {
  const records: DayRecord[] = [];
  recordsByDate.forEach((record, dateKey) => {
    const date = new Date(`${dateKey}T00:00:00`);
    if (isDateInViewRange(date, rangeStart, rangeEnd)) {
      records.push(record);
    }
  });
  return records;
};

export const getRecordsForMonth = (
  recordsByDate: Map<string, DayRecord>,
  month: Date,
  rangeStart: Date,
  rangeEnd: Date
) =>
  getRecordsInRange(recordsByDate, rangeStart, rangeEnd).filter((record) =>
    isSameMonth(new Date(`${record.date}T00:00:00`), month)
  );

export const positionTooltipNearCell = (cellElement: HTMLElement) => {
  const rect = cellElement.getBoundingClientRect();
  const padding = 12;

  let top = rect.bottom + TOOLTIP_GAP;
  let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;

  if (top + TOOLTIP_HEIGHT > window.innerHeight - padding) {
    top = rect.top - TOOLTIP_HEIGHT - TOOLTIP_GAP;
  }

  left = Math.max(
    padding,
    Math.min(left, window.innerWidth - TOOLTIP_WIDTH - padding)
  );
  top = Math.max(padding, top);

  return { top, left };
};

export const getCellModifier = (kind: CellKind) => {
  if (kind === 'Padding') return 'padding';
  if (kind === 'OutOfRange') return 'out-of-range';
  if (kind === 'Weekend') return 'weekend';
  return STATUS_CLASS[kind as DayStatus];
};
